/**
 * Project Detail → Timeline tab. 1:1 with the design folder's
 * `project-details.html#ptab-timeline`.
 *
 * Two stacked sections inside the tab content:
 *   1. Gantt chart card  — left sidebar (categories + tasks with %)
 *                          + right timeline (months/days header,
 *                          today line, colour-coded bars per task).
 *   2. Execution Milestones table — flat list of every task with
 *                          category pill, dates, progress bar, status pill.
 *
 * Data:
 *   - `useProjectTasks(orgId, projectId)` for the live task list
 *     (existing helper, mirrors mobile `useTasks`).
 *   - Tasks are grouped by `category` for the sidebar's
 *     "categories above tasks" rendering. Uncategorised tasks land
 *     in a "General" bucket.
 *   - Date range computed from the task min(startDate) /
 *     max(endDate). Falls back to a 30-day window centred on today
 *     when no tasks have dates.
 *
 * Interactions:
 *   - Click a bar → opens the task detail/edit slide-over (TBD —
 *     for v1 we surface an inline "Open task" link in the alert
 *     until the per-task slide-over ships).
 *
 * What this is NOT yet (clearly stubbed):
 *   - Drag-to-resize bars (the `.gantt-handle` elements are
 *     non-interactive in this iteration).
 *   - Per-task slide-over (we open the existing edit page when one
 *     lands; for now a tooltip + console log).
 *   - Recompute project progress on save (server triggers handle
 *     this automatically once `updateTask` writes).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { canEditTasks } from '@shared/perms';

import { LogWorkSlideOver } from '@/components/LogWorkSlideOver';
import { ProjectReportPanel } from '@/components/ProjectReportSlideOver';
import { TaskDetailSlideOver } from '@/components/TaskDetailSlideOver';
import { TaskFormSlideOver } from '@/components/TaskFormSlideOver';
import { updateTask } from '@/lib/tasks';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import {
  taskStatusLabel,
  useProjectTasks,
  type TaskItem,
} from '@/lib/useProjectTasks';
import type { ProjectListItem } from '@/lib/useProjects';
import { TimelineBoardView } from '@/routes/projectDetail/TimelineBoardView';
import { TimelineListView } from '@/routes/projectDetail/TimelineListView';

type ViewMode = 'calendar' | 'list' | 'board';

// ───── Constants ─────

/** Display labels for task categories. Mirrors mobile's
 *  `DEFAULT_TASK_CATEGORIES` (`src/features/tasks/taskCategories.ts`). */
const CATEGORY_LABELS: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  woodwork: 'Woodwork',
  design: 'Design',
  cleaning: 'Cleaning',
  floor_cleaning: 'Floor Cleaning',
  floor_matting: 'Floor Matting',
  painting: 'Painting',
  false_ceiling: 'False Ceiling',
  tiling: 'Tiling',
  general: 'General',
};

/** Tailwind-ish swatch keys that map to `.gantt-bar-container.{color}`
 *  in iio.css. Stable per category so the chart re-renders with
 *  consistent colours across snapshots. */
const CATEGORY_COLORS: Record<string, string> = {
  electrical: 'blue',
  plumbing: 'green',
  woodwork: 'brown',
  design: 'pink',
  cleaning: 'cyan',
  floor_cleaning: 'cyan',
  floor_matting: 'lightgreen',
  painting: 'orange',
  false_ceiling: 'orange',
  tiling: 'red',
  general: 'cyan',
};

const FALLBACK_COLORS = ['blue', 'green', 'cyan', 'orange', 'brown', 'lightgreen', 'red'];

function colorForCategory(key: string | undefined, idx: number): string {
  return (
    CATEGORY_COLORS[key ?? ''] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length]
  );
}

function labelForCategory(key: string | undefined): string {
  if (!key) return 'General';
  return CATEGORY_LABELS[key] ?? key.replace(/_/g, ' ');
}

// ───── Date helpers ─────

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function fmtShortDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function diffDaysInclusive(startMs: number, endMs: number): number {
  return Math.max(1, Math.round((endMs - startMs) / MS_PER_DAY) + 1);
}

// Stable string range like "May 2026 — Jun 2026" for the months row.
function monthsBetween(startMs: number, endMs: number): {
  label: string;
  days: number;
}[] {
  const out: { label: string; days: number }[] = [];
  let cursor = new Date(startMs);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(endMs);
  while (cursor.getTime() <= end.getTime()) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const last = new Date(year, month + 1, 0).getDate();
    // Clip the first / last month to the range.
    const firstDayOfBucket = Math.max(cursor.getTime(), startMs);
    const lastDayOfBucket = Math.min(
      new Date(year, month, last).getTime(),
      endMs,
    );
    out.push({
      label: cursor.toLocaleDateString('en-IN', {
        month: 'short',
        year: 'numeric',
      }),
      days: diffDaysInclusive(firstDayOfBucket, lastDayOfBucket),
    });
    cursor = new Date(year, month + 1, 1);
  }
  return out;
}

// ───── Grouping ─────

type CategoryGroup = {
  key: string;
  label: string;
  color: string;
  tasks: TaskItem[];
  /** Avg progress across child tasks (rounded). */
  progress: number;
};

function groupByCategory(tasks: TaskItem[]): CategoryGroup[] {
  const buckets = new Map<string, TaskItem[]>();
  for (const t of tasks) {
    const key = t.category ?? 'general';
    const arr = buckets.get(key) ?? [];
    arr.push(t);
    buckets.set(key, arr);
  }
  const out: CategoryGroup[] = [];
  let idx = 0;
  for (const [key, ts] of buckets) {
    // Sort tasks within a category by startDate asc (undated last).
    ts.sort((a, b) => {
      const am = a.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
      const bm = b.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
      return am - bm;
    });
    const avg = Math.round(
      ts.reduce((s, t) => s + (t.progress ?? 0), 0) / Math.max(1, ts.length),
    );
    out.push({
      key,
      label: labelForCategory(key),
      color: colorForCategory(key, idx),
      tasks: ts,
      progress: Math.max(0, Math.min(100, avg)),
    });
    idx += 1;
  }
  // Sort categories by earliest task start, undated last.
  out.sort((a, b) => {
    const am =
      a.tasks[0]?.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
    const bm =
      b.tasks[0]?.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
    return am - bm;
  });
  return out;
}

// ───── Chart range ─────

type GanttRange = {
  startMs: number;
  endMs: number;
  totalDays: number;
};

function computeRange(tasks: TaskItem[]): GanttRange {
  const today = startOfDay(Date.now());
  let minMs = Number.POSITIVE_INFINITY;
  let maxMs = Number.NEGATIVE_INFINITY;
  for (const t of tasks) {
    const s = t.startDate?.toMillis?.() ?? null;
    const e = t.endDate?.toMillis?.() ?? null;
    if (s != null && s < minMs) minMs = s;
    if (e != null && e > maxMs) maxMs = e;
  }
  if (!Number.isFinite(minMs)) minMs = today - 7 * MS_PER_DAY;
  if (!Number.isFinite(maxMs) || maxMs < minMs) {
    maxMs = minMs + 30 * MS_PER_DAY;
  }
  // Pad the right side a touch so the today line isn't flush against
  // the edge and tasks ending exactly on the last day stay visible.
  maxMs = maxMs + 2 * MS_PER_DAY;
  minMs = startOfDay(minMs);
  maxMs = startOfDay(maxMs);
  return {
    startMs: minMs,
    endMs: maxMs,
    totalDays: diffDaysInclusive(minMs, maxMs),
  };
}

function barPosition(
  t: TaskItem,
  range: GanttRange,
): { left: string; width: string } | null {
  const s = t.startDate?.toMillis?.();
  const e = t.endDate?.toMillis?.();
  if (s == null || e == null) return null;
  const sd = startOfDay(s);
  const ed = startOfDay(e);
  const offsetDays = Math.max(0, (sd - range.startMs) / MS_PER_DAY);
  const durationDays = Math.max(1, (ed - sd) / MS_PER_DAY + 1);
  return {
    left: `${(offsetDays / range.totalDays) * 100}%`,
    width: `${(durationDays / range.totalDays) * 100}%`,
  };
}

function projectBarPosition(
  group: CategoryGroup,
  range: GanttRange,
): { left: string; width: string } | null {
  let minMs = Number.POSITIVE_INFINITY;
  let maxMs = Number.NEGATIVE_INFINITY;
  for (const t of group.tasks) {
    const s = t.startDate?.toMillis?.();
    const e = t.endDate?.toMillis?.();
    if (s != null && s < minMs) minMs = s;
    if (e != null && e > maxMs) maxMs = e;
  }
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) return null;
  const sd = startOfDay(minMs);
  const ed = startOfDay(maxMs);
  const offsetDays = Math.max(0, (sd - range.startMs) / MS_PER_DAY);
  const durationDays = Math.max(1, (ed - sd) / MS_PER_DAY + 1);
  return {
    left: `${(offsetDays / range.totalDays) * 100}%`,
    width: `${(durationDays / range.totalDays) * 100}%`,
  };
}

// ───── Component ─────

type DragMode = 'move' | 'start' | 'end';
type DragState = {
  taskId: string;
  taskTitle: string;
  mode: DragMode;
  /** Mouse X at mousedown. */
  pxAtDown: number;
  /** Original startDate ms. */
  startMs: number;
  /** Original endDate ms. */
  endMs: number;
  /** Pixels-per-day, captured at mousedown. */
  pxPerDay: number;
  /** Live preview deltas (days). */
  previewDeltaDays: number;
  /** Live cursor X (for tooltip positioning, follows the mouse). */
  cursorX: number;
  /** Live cursor Y (for tooltip positioning, follows the mouse). */
  cursorY: number;
};

export function TimelineTab({ project }: { project: ProjectListItem }) {
  const orgId = project.orgId ?? null;
  const { tasks, loading, error } = useProjectTasks(orgId, project.id);
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canEditTasks(orgRole);

  // ── View mode (Calendar | List) ─────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');

  // ── Form slide-over (create + edit) ─────────────────────────
  const [formMode, setFormMode] = useState<
    { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; task: TaskItem }
  >({ kind: 'closed' });

  // ── Log Work slide-over ─────────────────────────────────────
  const [logWorkTask, setLogWorkTask] = useState<TaskItem | null>(null);

  // ── Per-task DETAIL slide-over (work-log feed + actions) ────
  const [detailTask, setDetailTask] = useState<TaskItem | null>(null);

  // ── Project Report slide-over ───────────────────────────────
  const [reportOpen, setReportOpen] = useState(false);

  // ── Drag-to-resize / reschedule ────────────────────────────
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dragError, setDragError] = useState<string | null>(null);

  const groups = useMemo(() => groupByCategory(tasks), [tasks]);
  const range = useMemo(() => computeRange(tasks), [tasks]);
  const months = useMemo(
    () => monthsBetween(range.startMs, range.endMs),
    [range],
  );

  // Today line position — null when today is outside the range.
  const today = startOfDay(Date.now());
  const todayLeft =
    today >= range.startMs && today <= range.endMs
      ? `${((today - range.startMs) / MS_PER_DAY / range.totalDays) * 100}%`
      : null;

  // Flatten the sidebar / timeline-body rows so the two columns stay
  // line-by-line aligned (1 category row + N task rows per group).
  type Row =
    | { kind: 'project'; group: CategoryGroup }
    | { kind: 'task'; group: CategoryGroup; task: TaskItem };
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const g of groups) {
      out.push({ kind: 'project', group: g });
      for (const t of g.tasks) out.push({ kind: 'task', group: g, task: t });
    }
    return out;
  }, [groups]);

  // Day-cell columns for the grid background (capped at 60 so the
  // chart stays readable on long projects).
  const gridCols = Math.min(60, Math.max(7, range.totalDays));

  // ── Drag handlers ───────────────────────────────────────────

  const beginDrag = useCallback(
    (e: React.MouseEvent, task: TaskItem, mode: DragMode) => {
      if (!bodyRef.current) return;
      const sMs = task.startDate?.toMillis?.();
      const eMs = task.endDate?.toMillis?.();
      if (sMs == null || eMs == null) return; // can't drag undated bars
      e.stopPropagation();
      e.preventDefault();
      const bodyRect = bodyRef.current.getBoundingClientRect();
      const pxPerDay = bodyRect.width / Math.max(1, range.totalDays);
      setDrag({
        taskId: task.id,
        taskTitle: task.title,
        mode,
        pxAtDown: e.clientX,
        startMs: sMs,
        endMs: eMs,
        pxPerDay,
        previewDeltaDays: 0,
        cursorX: e.clientX,
        cursorY: e.clientY,
      });
    },
    [range.totalDays],
  );

  // Mouse move + up listeners attached at window level while drag
  // is active so the user can drag outside the bar without losing
  // the gesture. Also flip the body's cursor + disable text
  // selection so the page doesn't visually fight the drag.
  useEffect(() => {
    if (!drag) return;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - drag.pxAtDown;
      const deltaDays = Math.round(dx / drag.pxPerDay);
      setDrag((cur) =>
        cur
          ? {
              ...cur,
              previewDeltaDays: deltaDays,
              cursorX: e.clientX,
              cursorY: e.clientY,
            }
          : cur,
      );
    };
    const onUp = async () => {
      const cur = drag;
      setDrag(null);
      if (!cur || cur.previewDeltaDays === 0) return;
      const deltaMs = cur.previewDeltaDays * MS_PER_DAY;
      let newStart = cur.startMs;
      let newEnd = cur.endMs;
      if (cur.mode === 'move') {
        newStart += deltaMs;
        newEnd += deltaMs;
      } else if (cur.mode === 'start') {
        newStart += deltaMs;
        // Don't allow start to jump past end.
        if (newStart > cur.endMs) newStart = cur.endMs;
      } else {
        newEnd += deltaMs;
        if (newEnd < cur.startMs) newEnd = cur.startMs;
      }
      try {
        await updateTask(cur.taskId, {
          startDate: new Date(startOfDay(newStart)),
          endDate: new Date(startOfDay(newEnd)),
        });
      } catch (err) {
        setDragError((err as Error).message);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [drag]);

  /** Apply the live drag preview to a bar's left/width. Returns the
   *  unmodified position when the bar isn't being dragged. */
  const previewPosition = (
    task: TaskItem,
    base: { left: string; width: string } | null,
  ): { left: string; width: string } | null => {
    if (!base) return base;
    if (!drag || drag.taskId !== task.id || drag.previewDeltaDays === 0) {
      return base;
    }
    const sMs = task.startDate?.toMillis?.();
    const eMs = task.endDate?.toMillis?.();
    if (sMs == null || eMs == null) return base;
    const deltaMs = drag.previewDeltaDays * MS_PER_DAY;
    let newStart = sMs;
    let newEnd = eMs;
    if (drag.mode === 'move') {
      newStart += deltaMs;
      newEnd += deltaMs;
    } else if (drag.mode === 'start') {
      newStart = Math.min(eMs, sMs + deltaMs);
    } else {
      newEnd = Math.max(sMs, eMs + deltaMs);
    }
    const offsetDays = Math.max(0, (startOfDay(newStart) - range.startMs) / MS_PER_DAY);
    const durationDays = Math.max(1, (startOfDay(newEnd) - startOfDay(newStart)) / MS_PER_DAY + 1);
    return {
      left: `${(offsetDays / range.totalDays) * 100}%`,
      width: `${(durationDays / range.totalDays) * 100}%`,
    };
  };

  if (loading) {
    return (
      <div
        className="card"
        style={{
          padding: 48,
          textAlign: 'center',
          color: 'var(--text-secondary)',
        }}
      >
        Loading timeline…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="card"
        style={{
          padding: 32,
          color: 'var(--critical)',
          background: 'var(--critical-bg)',
        }}
      >
        Couldn't load tasks: {error}
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <>
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
            <i className="ph ph-rocket-launch" />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            No timeline tasks yet
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              maxWidth: 380,
              margin: '0 auto 16px',
            }}
          >
            Add the first task to start tracking the project's timeline.
            Pick a category, start &amp; end dates, and you'll see a
            bar appear on the Gantt chart.
          </div>
          {canWrite ? (
            // Hidden for roles that can't write — Firestore rule blocks it anyway
            <button
              type="button"
              className="btn-primary"
              onClick={() => setFormMode({ kind: 'create' })}
            >
              <i className="ph-bold ph-plus" /> New Task
            </button>
          ) : null}
        </div>

        <TaskFormSlideOver
          open={formMode.kind !== 'closed'}
          onClose={() => setFormMode({ kind: 'closed' })}
          orgId={orgId}
          projectId={project.id}
          task={formMode.kind === 'edit' ? formMode.task : null}
        />
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Floating tooltip that follows the cursor during drag, showing
          the new start/end dates the task will land on. */}
      {drag ? (() => {
        const deltaMs = drag.previewDeltaDays * MS_PER_DAY;
        let newStart = drag.startMs;
        let newEnd = drag.endMs;
        if (drag.mode === 'move') {
          newStart += deltaMs;
          newEnd += deltaMs;
        } else if (drag.mode === 'start') {
          newStart = Math.min(drag.endMs, drag.startMs + deltaMs);
        } else {
          newEnd = Math.max(drag.startMs, drag.endMs + deltaMs);
        }
        const durDays = Math.max(
          1,
          Math.round((startOfDay(newEnd) - startOfDay(newStart)) / MS_PER_DAY) + 1,
        );
        const modeLabel =
          drag.mode === 'move'
            ? 'Move'
            : drag.mode === 'start'
              ? 'Resize start'
              : 'Resize end';
        return (
          <div
            style={{
              position: 'fixed',
              left: drag.cursorX + 14,
              top: drag.cursorY + 14,
              zIndex: 9999,
              background: 'var(--text-primary)',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.5,
              pointerEvents: 'none',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              fontFamily: 'inherit',
              minWidth: 200,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                opacity: 0.6,
                marginBottom: 2,
              }}
            >
              {modeLabel} · {drag.previewDeltaDays > 0 ? '+' : ''}
              {drag.previewDeltaDays} day
              {Math.abs(drag.previewDeltaDays) === 1 ? '' : 's'}
            </div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {drag.taskTitle}
            </div>
            <div style={{ display: 'flex', gap: 10, fontVariantNumeric: 'tabular-nums' }}>
              <span>
                <span style={{ opacity: 0.6 }}>Start</span>{' '}
                {fmtShortDate(startOfDay(newStart))}
              </span>
              <span style={{ opacity: 0.5 }}>→</span>
              <span>
                <span style={{ opacity: 0.6 }}>End</span>{' '}
                {fmtShortDate(startOfDay(newEnd))}
              </span>
            </div>
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>
              Duration: {durDays} day{durDays === 1 ? '' : 's'} ·
              Release to save
            </div>
          </div>
        );
      })() : null}

      {dragError ? (
        <div
          className="card"
          style={{
            padding: 12,
            background: 'var(--critical-bg)',
            color: 'var(--critical-text)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 13 }}>
            Couldn't save bar update: {dragError}
          </span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setDragError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* ─── View toolbar (Calendar | List + New Task) ─── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <ViewToggle value={viewMode} onChange={setViewMode} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn-secondary"
            style={{ height: 32, fontSize: 12, padding: '0 12px' }}
            onClick={() => setReportOpen(true)}
            disabled={tasks.length === 0}
            title={
              tasks.length === 0
                ? 'Add at least one task to generate a report'
                : 'Open project report (Progress Report + Timeline Agreement)'
            }
          >
            <i className="ph ph-file-text" /> Report
          </button>
          {canWrite ? (
            // Hidden for roles that can't write — Firestore rule blocks it anyway
            <button
              type="button"
              className="btn-primary"
              style={{ height: 32, fontSize: 12, padding: '0 12px' }}
              onClick={() => setFormMode({ kind: 'create' })}
            >
              <i className="ph-bold ph-plus" /> New Task
            </button>
          ) : null}
        </div>
      </div>

      <div
        style={
          reportOpen
            ? {
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) 360px',
                gap: 16,
                alignItems: 'start',
              }
            : undefined
        }
      >
        <div style={{ minWidth: 0 }}>
      {viewMode === 'list' ? (
        <TimelineListView
          tasks={tasks}
          onEditTask={(task) => setDetailTask(task)}
          onPostUpdate={(task) => setLogWorkTask(task)}
        />
      ) : viewMode === 'board' ? (
        <TimelineBoardView
          tasks={tasks}
          onEditTask={(task) => setDetailTask(task)}
          onPostUpdate={(task) => setLogWorkTask(task)}
        />
      ) : (
      <>
      {/* ─── Gantt chart card ─── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          className="card-header"
          style={{
            borderBottom: '1px solid var(--border)',
            background: '#fff',
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className="card-title">Project Timeline (Gantt)</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Drag <i className="ph-bold ph-dots-six-vertical" /> to move ·
            drag <i className="ph-bold ph-caret-double-right" /> to resize ·
            click bar to edit
          </div>
        </div>

        <div className="gantt-wrapper" style={{ borderRadius: 0, border: 'none', boxShadow: 'none', height: 'auto', minHeight: 420 }}>
          {/* LEFT — sidebar */}
          <div className="gantt-sidebar">
            <div className="gantt-header-row">
              <div className="gh-col gh-name">Task Categories &amp; Items</div>
              <div className="gh-col gh-pct">% Done</div>
            </div>
            <div className="gantt-sidebar-body">
              {rows.map((row, i) => {
                if (row.kind === 'project') {
                  return (
                    <div key={`p:${row.group.key}:${i}`} className="gantt-row project-row">
                      <div className="gantt-cell gc-name">
                        {row.group.label}
                      </div>
                      <div className="gantt-cell gc-pct">
                        {row.group.progress}%
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={`t:${row.task.id}`}
                    className="gantt-row task-row"
                    onClick={() =>
                      setDetailTask(row.task)
                    }
                    style={{ cursor: 'pointer' }}
                    title={`Edit ${row.task.title}`}
                  >
                    <div className="gantt-cell gc-name">{row.task.title}</div>
                    <div className="gantt-cell gc-pct">
                      {row.task.progress ?? 0}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — timeline */}
          <div className="gantt-timeline-container">
            <div className="gantt-timeline">
              {/* Months header */}
              <div className="gantt-months">
                {months.map((m, i) => (
                  <div
                    key={`m:${i}`}
                    className="gantt-month"
                    style={{ flex: m.days }}
                  >
                    {m.label}
                  </div>
                ))}
              </div>
              {/* Days header — render a tick per day, but cap when very
                  long so labels stay legible. */}
              <div className="gantt-days">
                {Array.from({ length: gridCols }).map((_, i) => {
                  // Map column → day label (date inside the range).
                  const dayMs =
                    range.startMs +
                    Math.round((i * range.totalDays) / gridCols) * MS_PER_DAY;
                  const d = new Date(dayMs);
                  return (
                    <div key={`d:${i}`} className="gantt-day">
                      {d.getDate()}
                    </div>
                  );
                })}
              </div>
              {/* Body — rows + bars + today line */}
              <div className="gantt-timeline-body" ref={bodyRef}>
                {/* Vertical grid background */}
                <div className="gantt-grid-bg">
                  {Array.from({ length: gridCols }).map((_, i) => (
                    <div key={`g:${i}`} className="gantt-grid-col" />
                  ))}
                </div>

                {/* Today line + label */}
                {todayLeft ? (
                  <>
                    <div
                      className="gantt-today-line"
                      style={{ left: todayLeft }}
                    />
                    <div
                      className="gantt-today-label"
                      style={{ left: todayLeft }}
                    >
                      TODAY
                    </div>
                  </>
                ) : null}

                {/* Rows */}
                {rows.map((row, i) => {
                  if (row.kind === 'project') {
                    const pos = projectBarPosition(row.group, range);
                    return (
                      <div
                        key={`tlp:${row.group.key}:${i}`}
                        className="gantt-tl-row project-row"
                      >
                        {pos ? (
                          <div
                            className={`gantt-bar-container ${row.group.color}`}
                            style={{
                              left: pos.left,
                              width: pos.width,
                              opacity: 0.7,
                              cursor: 'default',
                            }}
                            title={`${row.group.label} · ${row.group.progress}%`}
                          >
                            {row.group.label}
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                  const basePos = barPosition(row.task, range);
                  const pos = previewPosition(row.task, basePos);
                  const beingDragged =
                    drag?.taskId === row.task.id;
                  return (
                    <div key={`tl:${row.task.id}`} className="gantt-tl-row">
                      {pos ? (
                        <div
                          className={`gantt-bar-container ${row.group.color}`}
                          style={{
                            left: pos.left,
                            width: pos.width,
                            // Bar body itself is NOT a drag target anymore —
                            // the explicit grip + chevron icons own move /
                            // resize. Body click = edit.
                            cursor: 'pointer',
                            transform: beingDragged ? 'scale(1.02)' : undefined,
                            boxShadow: beingDragged
                              ? '0 6px 16px rgba(0,0,0,0.25)'
                              : undefined,
                          }}
                          title={`${row.task.title}\n${fmtShortDate(
                            row.task.startDate?.toMillis?.() ?? null,
                          )} → ${fmtShortDate(
                            row.task.endDate?.toMillis?.() ?? null,
                          )}\n\nClick to edit · drag ⠿ grip to move · drag » chevron to resize`}
                          onClick={(e) => {
                            // If the drag started, mouseup already fired and
                            // setFormMode would conflict — bail when delta=0
                            // means it was a true click, not a click that
                            // followed a drag.
                            if (e.detail === 0) return;
                            setDetailTask(row.task);
                          }}
                        >
                          {/* LEFT GRIP — drag to move. Hidden for read-only
                              roles (Supervisor etc.) so they can't grab a
                              drag handle that would fail on save. */}
                          {canWrite ? (
                            <span
                              className="gantt-grip"
                              onMouseDown={(e) =>
                                beginDrag(e, row.task, 'move')
                              }
                              onClick={(e) => e.stopPropagation()}
                              title="Drag to move"
                              aria-label="Drag to move"
                            >
                              <i className="ph-bold ph-dots-six-vertical" />
                            </span>
                          ) : null}

                          {/* LABEL — flexes to fill, click to edit */}
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              paddingRight: 24,
                            }}
                          >
                            {row.task.title}
                          </span>

                          {/* LEFT EDGE HANDLE — bonus power-user resize
                              for start date. Visible on hover only. Hidden
                              for read-only roles. */}
                          {canWrite ? (
                            <div
                              className="gantt-handle left"
                              onMouseDown={(e) =>
                                beginDrag(e, row.task, 'start')
                              }
                              onClick={(e) => e.stopPropagation()}
                              title="Drag to change start date"
                              style={{ opacity: beingDragged ? 1 : undefined }}
                            />
                          ) : null}

                          {/* RIGHT CHEVRON — drag to resize end. Hidden for
                              read-only roles. */}
                          {canWrite ? (
                            <span
                              className="gantt-resize"
                              onMouseDown={(e) =>
                                beginDrag(e, row.task, 'end')
                              }
                              onClick={(e) => e.stopPropagation()}
                              title="Drag to resize"
                              aria-label="Drag to resize end date"
                            >
                              <i className="ph-bold ph-caret-double-right" />
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setDetailTask(row.task)
                          }
                          style={{
                            position: 'absolute',
                            left: 16,
                            top: 8,
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            fontStyle: 'italic',
                            background: 'none',
                            border: '1px dashed var(--border)',
                            borderRadius: 4,
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                          title="Set dates"
                        >
                          No dates · click to set
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Execution Milestones table ─── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          className="card-header"
          style={{
            borderBottom: '1px solid var(--border)',
            background: '#fff',
            padding: '16px 20px',
          }}
        >
          <div className="card-title">
            Execution Milestones · {tasks.length}
          </div>
        </div>
        <table className="proj-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Task / Milestone</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th style={{ width: 180 }}>Progress</th>
              <th>Status</th>
              <th style={{ width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((g) =>
              g.tasks.map((t) => {
                const pct = Math.max(0, Math.min(100, t.progress ?? 0));
                const fill =
                  t.status === 'completed'
                    ? 'var(--success)'
                    : t.status === 'ongoing'
                      ? 'var(--brand)'
                      : 'var(--border)';
                const pillClass =
                  t.status === 'completed'
                    ? 'stage-completed'
                    : t.status === 'ongoing'
                      ? 'stage-ongoing'
                      : 'stage-pending';
                return (
                  <tr
                    key={t.id}
                    onClick={() => setDetailTask(t)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--text-secondary)',
                          background: '#F3F4F6',
                          padding: '4px 8px',
                          borderRadius: 4,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {g.label}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{t.title}</td>
                    <td>{fmtShortDate(t.startDate?.toMillis?.() ?? null)}</td>
                    <td>{fmtShortDate(t.endDate?.toMillis?.() ?? null)}</td>
                    <td>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <div
                          className="spent-bar"
                          style={{ width: '100%', height: 6 }}
                        >
                          <div
                            className="spent-bar-fill"
                            style={{
                              width: `${pct}%`,
                              background: fill,
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--text-secondary)',
                            fontVariantNumeric: 'tabular-nums',
                            minWidth: 32,
                            textAlign: 'right',
                          }}
                        >
                          {pct}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`stage-pill ${pillClass}`}>
                        {taskStatusLabel(t.status)}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogWorkTask(t);
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid var(--brand)',
                          background: '#fff',
                          color: 'var(--brand)',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                        }}
                        title="Post a work log update"
                      >
                        <i className="ph-bold ph-plus" /> Log
                      </button>
                    </td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

        </div>

        {/* Docked Report rail (right side, sticky). Toggled by the
            "Report" button. The same content also still works as a
            full slide-over via ProjectReportSlideOver, but this
            docked variant keeps the Calendar / List view visible
            alongside, matching the Party tab's right-rail pattern. */}
        {reportOpen ? (
          <div
            className="card"
            style={{
              padding: 0,
              overflow: 'hidden',
              position: 'sticky',
              top: 16,
              // Concrete height (not maxHeight) so the flex column
              // inside knows what 100% means and the body's
              // overflowY: auto can actually trigger.
              height: 'calc(100vh - 100px)',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}
          >
            <ProjectReportPanel
              project={project}
              tasks={tasks}
              onClose={() => setReportOpen(false)}
              variant="rail"
            />
          </div>
        ) : null}
      </div>

      {/* Task form slide-over (create + edit) */}
      <TaskFormSlideOver
        open={formMode.kind !== 'closed'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        projectId={project.id}
        task={formMode.kind === 'edit' ? formMode.task : null}
      />

      {/* Log Work slide-over — opened by per-task "Log work" buttons.
          Writes to tasks/{taskId}/updates and rolls progress + status
          onto the parent task. Same writes the mobile "Post update"
          sheet makes, so the Site tab Work Logs feed reflects it
          within ~1s on both clients. */}
      <LogWorkSlideOver
        open={Boolean(logWorkTask)}
        onClose={() => setLogWorkTask(null)}
        task={logWorkTask}
      />

      {/* Per-task DETAIL slide-over — clicking any task row in
          Calendar / List / Board opens this. Shows task hero +
          reference photos + full work-log feed (`tasks/{id}/updates`).
          Has its own Edit + Post Update buttons that mount the
          form / log-work modals on top. */}
      <TaskDetailSlideOver
        open={Boolean(detailTask)}
        onClose={() => setDetailTask(null)}
        orgId={orgId}
        projectId={project.id}
        task={detailTask}
      />

    </div>
  );
}

// ───── View toggle (Calendar | List) ─────

function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const options: { key: ViewMode; label: string; icon: string }[] = [
    { key: 'calendar', label: 'Calendar', icon: 'ph ph-calendar-blank' },
    { key: 'board', label: 'Board', icon: 'ph ph-kanban' },
    { key: 'list', label: 'List', icon: 'ph ph-list' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Timeline view"
      style={{
        display: 'inline-flex',
        padding: 3,
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: '#FAFAFA',
        gap: 2,
      }}
    >
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              border: 'none',
              background: active ? '#fff' : 'transparent',
              color: active ? 'var(--brand)' : 'var(--text-secondary)',
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              transition: 'background 0.15s, color 0.15s',
              fontFamily: 'inherit',
            }}
          >
            <i className={o.icon} /> {o.label}
          </button>
        );
      })}
    </div>
  );
}
